import { jsonPost } from '../json-request';
import type { ValidatedApiClient } from '../types/api-client';
import {
    GAMIFY_OBJECTIVE_CLAIM_API_PATH,
    GAMIFY_OBJECTIVE_SETS_CURRENT_API_PATH,
    GAMIFY_RECURRING_REWARDS_API_PATH,
    GAMIFY_STREAK_API_PATH,
    GAMIFY_STREAK_CLAIM_API_PATH,
    gamifyRecurringRewardClaimApiPath,
} from './constants';
import { lootboxesResponseSchema, openLootboxResponseSchema } from './lootbox-schema';
import {
    loginStreakAwardSchema,
    loginStreakRewardProgressSchema,
    type ObjectiveClaimRequest,
    objectiveClaimResponseSchema,
    objectiveSetsSchema,
    recurringRewardClaimResponseSchema,
    recurringRewardsSchema,
} from './schema';

export const createGamifyApi = ({ validatedFetch }: ValidatedApiClient) => ({
    getLootboxes: (timezone: string) =>
        validatedFetch(lootboxesResponseSchema, '/v3/gamify/lootboxes', new URLSearchParams({ timezone }), {
            cache: 'no-store',
        }),

    openLootbox: (source: string, timezone: string) =>
        validatedFetch(
            openLootboxResponseSchema,
            `/v3/gamify/lootboxes/${encodeURIComponent(source)}/open`,
            new URLSearchParams(),
            jsonPost({ timezone }),
        ),

    // Polled for claimable state, so it must never be answered from the HTTP cache.
    getCurrentObjectiveSets: () =>
        validatedFetch(objectiveSetsSchema, GAMIFY_OBJECTIVE_SETS_CURRENT_API_PATH, new URLSearchParams(), {
            cache: 'no-store',
        }),

    claimObjectiveReward: ({ id }: ObjectiveClaimRequest) =>
        validatedFetch(
            objectiveClaimResponseSchema,
            GAMIFY_OBJECTIVE_CLAIM_API_PATH,
            new URLSearchParams(),
            jsonPost({ id }),
        ),

    getStreak: () =>
        validatedFetch(loginStreakRewardProgressSchema, GAMIFY_STREAK_API_PATH, new URLSearchParams(), {
            cache: 'no-store',
        }),

    // The endpoint takes no body, so this skips `jsonPost({})`, which would send an empty one.
    claimStreakReward: () =>
        validatedFetch(loginStreakAwardSchema, GAMIFY_STREAK_CLAIM_API_PATH, new URLSearchParams(), {
            method: 'POST',
        }),

    getRecurringRewards: () =>
        validatedFetch(recurringRewardsSchema, GAMIFY_RECURRING_REWARDS_API_PATH, new URLSearchParams(), {
            cache: 'no-store',
        }),

    claimRecurringReward: (reward: string) =>
        validatedFetch(
            recurringRewardClaimResponseSchema,
            gamifyRecurringRewardClaimApiPath(reward),
            new URLSearchParams(),
            { method: 'POST' },
        ),
});

export type GamifyApi = ReturnType<typeof createGamifyApi>;
